import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RotateCcw } from 'lucide-react';
import { environments as allEnvironments } from '../content/environments';
import type { Environment } from '../content/environments';
import { fuzzyFilter } from '../search/fuzzySearch';
import { useStrings } from '../locales';
import { SortIcon, SortState } from '../components/SortIcon';
import { useNewSourceFilter } from '../hooks/useNewSourceFilter';
import { StatBox, StatBrowser, StatFeature } from '../components/StatBlock';
import './Environments.css';

/* ── Colour maps, real hex so ${color}22 opacity trick works ─────── */
const TYPE_COLORS: Record<string, string> = {
  Exploration: '#4db8c8',
  Social: '#c9a227',
  Traversal: '#e07070',
  Event: '#9b5fdd',
};

// TYPE_DESCS moved to locale strings (s.environments.typeDescs)

const TIER_COLORS: Record<string, string> = { '1': '#a89cc8', '2': '#60d890', '3': '#e8c547', '4': '#f06ab0' };

const ALL_TYPES = ['All', 'Exploration', 'Social', 'Traversal', 'Event'];
const ALL_TIERS = ['All', '1', '2', '3', '4'];


const DEFAULT_SORT: SortState = { col: '', dir: 'asc' };


/* ── Multi-select helpers ────────────────────────────────────────── */
function toggleFilter(current: Set<string>, value: string): Set<string> {
  if (value === 'All') return new Set(['All']);
  const next = new Set(current);
  next.delete('All');
  if (next.has(value)) {
    next.delete(value);
    if (next.size === 0) next.add('All');
  } else {
    next.add(value);
  }
  return next;
}
function passesFilter(set: Set<string>, value: string): boolean { return set.has('All') || set.has(value); }
function isDefault(s: Set<string>): boolean { return s.size === 1 && s.has('All'); }

/* ── Sort helpers ────────────────────────────────────────────────── */
function sortEnvironments(list: Environment[], sort: SortState): Environment[] {
  if (!sort.col) return list;
  const d = sort.dir === 'asc' ? 1 : -1;
  return [...list].sort((a, b) => {
    switch (sort.col) {
      case 'name': return d * a.name.localeCompare(b.name);
      case 'type': return d * (a.type || '').localeCompare(b.type || '') || a.name.localeCompare(b.name);
      case 'difficulty': {
        const da = isNaN(Number(a.difficulty)) ? -Infinity : Number(a.difficulty);
        const db = isNaN(Number(b.difficulty)) ? -Infinity : Number(b.difficulty);
        return d * (da - db) || a.name.localeCompare(b.name);
      }
      case 'features': return d * ((a.features?.length || 0) - (b.features?.length || 0)) || a.name.localeCompare(b.name);
      case 'tier': return d * (a.tier - b.tier) || a.name.localeCompare(b.name);
      default: return 0;
    }
  });
}

/* ── Sort icon (up/down triangles, dimmed when inactive) ─────────── */

/* ── Reset button ────────────────────────────────────────────────── */
function ResetBtn({ dirty, onClick }: { dirty: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      disabled={!dirty}
      className={`reset-btn ${dirty ? 'reset-btn-dirty' : ''}`}
      title="Reset all filters and sorting"
    >
      <RotateCcw size={13} /> Reset
    </button>
  );
}

/* ── Stat card, laid out like the SRD stat block ───────────────────── */
function EnvStatCard({ env }: { env: Environment }) {
  const s = useStrings().environments;
  return (
    <article className="srdcard">
      <h2 className="srdcard__name">{env.name}</h2>
      <p className="srdcard__kind">{`Tier ${env.tier} ${env.type}`}</p>
      <p className="srdcard__desc">{env.description}</p>
      <p className="statblock__line"><strong>{s.impulsesLabel}</strong> {env.impulses}</p>
      <StatBox
        rows={[
          [{ label: s.difficultyLabel, value: env.difficulty }],
          ...(env.potentialAdversaries ? [[{ label: s.potentialAdversariesLabel, value: env.potentialAdversaries }]] : []),
        ]}
      />
      <h3 className="srdcard__features">{s.featuresTitle}</h3>
      {env.features.map((f, i) => <StatFeature key={i} feature={f} />)}
      <span className="source-label">{env.source === 'void' ? 'Void' : 'Core'}</span>
    </article>
  );
}

/* ════════════════════════════════════════════════════════════════
   Main page
   ════════════════════════════════════════════════════════════════ */
export default function Environments() {
  const s = useStrings().environments;
  const TYPE_DESCS = s.typeDescs;
  const SORT_COLS_ENV = [
    { col: 'tier', label: s.sortCols.tier },
    { col: 'name', label: s.sortCols.name },
    { col: 'type', label: s.sortCols.type },
    { col: 'difficulty', label: s.sortCols.difficulty },
    { col: 'features', label: s.sortCols.features },
  ];
  const [searchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);
  const [typeFilter, setTypeFilter] = useState(new Set(['All']));
  const [tierFilter, setTierFilter] = useState(new Set(['All']));
  const [sourceFilter, setSourceFilter] = useState('All');
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [sort, setSort] = useState(DEFAULT_SORT);
  const { glows, onSourceClick } = useNewSourceFilter('/environments');

  useEffect(() => {
    const q = searchParams.get('q');
    if (q) setSearch(q);
  }, [searchParams]);

  const handleSort = (col: string) =>
    setSort((prev): SortState => prev.col === col
      ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { col, dir: 'asc' }
    );

  const isSortDirty = sort.col !== '';
  const dirty = !!search || !isDefault(typeFilter) || !isDefault(tierFilter) || sourceFilter !== 'All' || isSortDirty;

  const reset = () => {
    setSearch(''); setTypeFilter(new Set(['All']));
    setTierFilter(new Set(['All'])); setSourceFilter('All'); setSort(DEFAULT_SORT);
  };

  const filtered = useMemo(() => {
    const base = allEnvironments.filter((e) => {
      if (!passesFilter(typeFilter, e.type)) return false;
      if (!passesFilter(tierFilter, String(e.tier))) return false;
      if (sourceFilter === 'Void') return e.source === 'void';
      if (sourceFilter === 'Core') return !e.source;
      return true;
    });
    return fuzzyFilter(base, search,
      (e) => [e.description, e.impulses, ...(e.features || [])].join(' '),
      (e) => e.name
    );
  }, [typeFilter, tierFilter, sourceFilter, search]);

  const sorted = useMemo(
    () => search.trim() ? filtered : sortEnvironments(filtered, sort),
    [filtered, sort, search]
  );

  const useTierGroups = sort.col === 'tier' && !search;
  const tierOrder = sort.dir === 'asc' ? [1, 2, 3, 4] : [4, 3, 2, 1];

  return (
    <div className="page-content">
      <div className="page-header">
        <h1>{s.title}</h1>
        <p>{s.desc}</p>
      </div>

      {/* Type legend */}
      <div className="type-legend">
        <div className="type-legend-header">Environment Types</div>
        <div className="type-legend-grid">
          {Object.entries(TYPE_DESCS).map(([type, desc]) => {
            const color = TYPE_COLORS[type];
            return (
              <div key={type} className="type-legend-item" style={{ borderLeftColor: color }}>
                <div className="type-legend-title" style={{ color }}>{type}</div>
                <div className="type-legend-desc">{desc}</div>
              </div>
            );
          })}
        </div>
      </div>

      <input type="text" id="environments-search" name="environments-search" aria-label={s.searchAria} placeholder={s.searchPlaceholder} value={search}
        onChange={(e) => setSearch(e.target.value)} className="search-input filter-search-bar" />

      <div className="filter-layout">

        {/* Mobile filter toggle row (toggle + reset side by side) */}
        <div className="filter-toggle-row">
          <button
            className="filter-toggle-btn"
            onClick={() => setShowFilters(f => !f)}
            aria-expanded={showFilters}
            aria-controls="filter-panel"
          >
            <span>⚙ Filters{dirty ? ' •' : ''}</span>
            <span>{showFilters ? '▲' : '▼'}</span>
          </button>
          <ResetBtn dirty={dirty} onClick={reset} />
        </div>

        {/* Mobile active filter chips */}
        {(() => {
          const chips = [
            ...(search ? [{ label: `"${search}"`, color: 'var(--gold)', onRemove: () => setSearch('') }] : []),
            ...[...typeFilter].filter(v => v !== 'All').map(v => ({ label: v, color: TYPE_COLORS[v], onRemove: () => setTypeFilter(p => toggleFilter(p, v)) })),
            ...[...tierFilter].filter(v => v !== 'All').map(v => ({ label: `T${v}`, color: TIER_COLORS[v], onRemove: () => setTierFilter(p => toggleFilter(p, v)) })),
            ...(sourceFilter !== 'All' ? [{ label: sourceFilter, color: 'var(--text-muted)', onRemove: () => setSourceFilter('All') }] : []),
          ];
          return chips.length > 0 ? (
            <div className="mobile-filter-chips">
              {chips.map(({ label, color, onRemove }) => {
                const c = color || 'var(--gold)';
                return (
                  <span key={label} className="filter-chip" style={{ background: `${c}18`, border: `1px solid ${c}44`, color: c }}>
                    {label}
                    <button onClick={onRemove} className="filter-chip-remove" style={{ color: c }}>×</button>
                  </span>
                );
              })}
            </div>
          ) : null;
        })()}

        {/* ── Left panel ── */}
        <div id="filter-panel" className={`filter-sidebar${showFilters ? '' : ' filter-sidebar--hidden'}`}>
          <div className="filter-sidebar-header">
            <span className="filter-label">Filters</span>
            <span className="hide-mobile"><ResetBtn dirty={dirty} onClick={reset} /></span>
          </div>

          <div className="filter-count" aria-live="polite" aria-atomic="true">
            {filtered.length} of {allEnvironments.length}
          </div>

          {/* Type, vertical multi-select with accent colors */}
          <div style={{ marginBottom: '16px' }}>
            <div className="filter-label" style={{ display: 'block', marginBottom: '6px' }}>Type</div>
            <div className="filter-pill-group">
              {ALL_TYPES.map((t) => {
                const active = typeFilter.has(t);
                const accent = t !== 'All' ? TYPE_COLORS[t] : null;
                return (
                  <button key={t} onClick={() => setTypeFilter(toggleFilter(typeFilter, t))}
                    className={`filter-pill ${active ? 'filter-pill-active' : 'filter-pill-inactive'}`}
                    aria-pressed={active}
                    style={active && accent ? { background: `${accent}22`, borderColor: accent, color: accent } : undefined}>
                    {t}
                  </button>
                );
              })}
            </div>
          </div>

        </div>

        {/* ── Right panel ── */}
        <div className="filter-right-panel">

          {/* Horizontal filter + sort bar, matches Equipment style */}
          <div className="filter-bar">

            {/* Tier filter */}
            <div className="filter-group">
              <span className="filter-label" style={{ marginRight: '2px' }}>Tier</span>
              {ALL_TIERS.map((t) => (
                <button key={t} className={`filter-btn ${tierFilter.has(t) ? 'filter-btn-active' : ''}`}
                  aria-pressed={tierFilter.has(t)}
                  onClick={() => setTierFilter(toggleFilter(tierFilter, t))}>
                  {t === 'All' ? 'All' : `T${t}`}
                </button>
              ))}
            </div>

            <div className="filter-divider" />

            {/* Source filter */}
            <div className="filter-group">
              <span className="filter-label" style={{ marginRight: '2px' }}>Source</span>
              {['All', 'Core', 'Void'].map((src) => (
                <button key={src}
                  className={`filter-btn ${sourceFilter === src ? 'filter-btn-active' : ''}${glows(src) ? ' new-source-glow' : ''}`}
                  aria-pressed={sourceFilter === src}
                  onClick={() => { setSourceFilter(src); onSourceClick(src); }}>
                  {src}
                </button>
              ))}
            </div>

            <div className="filter-divider" />

            {/* Sort */}
            <div className="filter-group">
              <span className="filter-label" style={{ marginRight: '2px' }}>Sort</span>
              {SORT_COLS_ENV.map(({ col, label }) => (
                <button key={col} className={`filter-btn ${sort.col === col ? 'filter-btn-active' : ''}`}
                  onClick={() => handleSort(col)}>
                  {label}<SortIcon col={col} sort={sort} />
                </button>
              ))}
            </div>
          </div>

          {filtered.length === 0 && (
            <div className="empty-state" role="status"><p>{s.noMatch}</p></div>
          )}

          <StatBrowser
            groups={useTierGroups
              ? tierOrder.map((tier) => {
                const items = sorted.filter((e) => e.tier === tier);
                return { key: `tier-${tier}`, label: <><span>Tier {tier}</span><span>{items.length}</span></>, items };
              })
              : [{ key: 'all', items: sorted }]}
            keyOf={(env) => env.name}
            accentOf={(env) => TYPE_COLORS[env.type] || 'var(--border-light)'}
            renderItem={(env) => (
              <>
                <span className="statview__name">{env.name}</span>
                <span className="statview__meta">{env.type} · Difficulty {env.difficulty}</span>
              </>
            )}
            renderCard={(env) => <EnvStatCard env={env} />}
            listLabel={s.title}
            closeLabel={s.backToList}
          />
        </div>
      </div>
    </div>
  );
}